import clsx from 'clsx';
import { forwardRef } from 'react';
import { useIntl } from 'react-intl';

import {
  CriticalIcon,
  InformationIcon,
  SuccessIcon,
  WarningIcon,
} from '@dynatrace/strato-icons';

import { useInformationOverlayContext } from './hooks/useInformationOverlayContext.js';
import { COMPONENT_NAME } from './information-overlay-constants.js';
import {
  InformationOverlayTriggerCSS,
  InformationOverlayTriggerIconCSS,
  InformationOverlayTriggerIconHoverCSS,
  InformationOverlayTriggerIconVariantsCSS,
  InformationOverlayTriggerTextCSS,
} from './InformationOverlay.sty.js';
import { InformationOverlayIcon } from './InformationOverlayIcon.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useId } from '../../core/hooks/useId.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';

//#region Typings
/**
 * @public
 */
export interface InformationOverlayTriggerProps
  extends DOMProps,
    WithChildren,
    StylingProps,
    MaskingProps,
    DataTestId,
    BehaviorTrackingProps,
    AriaLabelingProps {}
//#endregion Typings

/**
 * InformationOverlay Trigger component, used for rendering trigger content.
 * @public
 */
export const Trigger = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  InformationOverlayTriggerProps
>((props, forwardedRef) => {
  const {
    children,
    id: propId,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;
  const { color, overlayTriggerProps } = useInformationOverlayContext();
  const id = useId();

  const { focusProps, focusClassName, isFocusVisible } = useFocusRing({
    color: children ? 'neutral' : color,
    isMinimal: true,
  });

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const {
    'data-dt-name': dataDtName = 'Trigger',
    ...remainingBehavioralTrackingProps
  } = behavioralTrackingProps;
  const { 'aria-label': ariaLabelProp, ...remainingAriaLabelingProps } =
    useAriaLabelingProps(restProps);

  const [slots, freeform] = useSlots(children, {
    icon: InformationOverlayIcon,
  });
  const hasFreeform =
    freeform.filter(
      (child) => child !== '' && child !== undefined && child !== null,
    ).length > 0;

  if (slots.icon === undefined) {
    switch (color) {
      case 'success':
        slots.icon = <SuccessIcon />;
        break;
      case 'warning':
        slots.icon = <WarningIcon />;
        break;
      case 'critical':
        slots.icon = <CriticalIcon />;
        break;
      default:
        slots.icon = <InformationIcon />;
        break;
    }
  }

  // If the trigger gets string children, then there is
  // 1. no need for an aria label
  // 2. The aria label would need to at least contain the parts of the children string based on https://accessibilityinsights.io/info-examples/web/needs-review/label-content-name-mismatch/
  const intl = useIntl();
  let ariaLabel = ariaLabelProp;

  if (
    ariaLabel === undefined &&
    (children === undefined || !_canReceiveDefaultTextFormat(children))
  ) {
    ariaLabel = intl.formatMessage({
      defaultMessage: 'Open additional information.',
      id: 'NXdoElYQZNKea1Sc',
      description: 'Aria label fallback for Information overlay trigger',
    });
  }

  return (
    <button
      ref={forwardedRef}
      aria-haspopup="dialog"
      aria-label={ariaLabel}
      id={propId ?? id}
      {..._mergeProps(
        overlayTriggerProps,
        focusProps,
        remainingAriaLabelingProps,
        remainingBehavioralTrackingProps,
      )}
      className={clsx(InformationOverlayTriggerCSS, consumerClassName)}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-dt-name={dataDtName}
      data-dt-component={COMPONENT_NAME}
    >
      <InformationOverlayIcon
        className={clsx(
          InformationOverlayTriggerIconCSS,
          InformationOverlayTriggerIconVariantsCSS[color],
          {
            [focusClassName]: !hasFreeform,
            [InformationOverlayTriggerIconHoverCSS]: !hasFreeform,
          },
          { 'strato-focus-visible': isFocusVisible },
        )}
      >
        {slots.icon}
      </InformationOverlayIcon>
      {hasFreeform && (
        <span
          className={clsx(focusClassName, InformationOverlayTriggerTextCSS, {
            'strato-focus-visible': isFocusVisible,
          })}
          style={{
            flex: 1,
            overflow: 'hidden',
            textDecoration: isFocusVisible ? 'none' : undefined,
          }}
        >
          {freeform}
        </span>
      )}
    </button>
  );
});

(Trigger as typeof Trigger & { displayName: string }).displayName =
  'InformationOverlay.Trigger';
